import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { LabeledValue } from "components/designV2/labeledValue/labeledValue";
import { Modal } from "components/designV2/modal/modal";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { StatusBanner } from "components/designV2/statusBanner/statusBanner";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { CompanyUser, UserRole } from "model/Company";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { CompanyUserForm } from "./companyUserForm.component";
import {
  ActionButtons,
  AddUserButtonSlot,
  CompanyUsersHeader,
  CompanyUsersList,
  CompanyUsersTitle,
  EmailCell,
  IconButton,
  ListMessage,
  LoadingContainer,
  ModalActions,
  UserActionsCell,
  UserRow,
  UserTypeCell,
} from "./companyUsers.styled";

type Props = {
  view?: string;
};

const getDisplayName = (user: CompanyUser): string => {
  const displayName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return displayName.length > 0 ? displayName : user.email;
};

export const CompanyUsers: React.FC<Props> = () => {
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();
  const [companyState, companyActions, companyDispatch] = useCompanyStore();
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<CompanyUser | null>(null);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (companyState.company?.id) {
      Api()
        .company.getCompanyUsers(companyState.company.id)
        .then((res) => {
          companyDispatch({ type: "GET_COMPANY_USERS", payload: res.data });
        });
    }
  }, [companyDispatch, companyState.company?.id]);

  const handleRemoveUser = (userId: number): void => {
    if (companyState.company) {
      setIsLoading(true);
      companyActions
        .removeCompanyUser(companyState.company.id, userId)
        .then(() => {
          setCurrentUser(null);
          setShowDeleteUserModal(false);
          setIsLoading(false);
        })
        .catch(() => {
          alertDispatch({
            type: "SHOW_ALERT",
            payload: {
              icon: "alert",
              title: t("Alerts.OhNo"),
              message: t("CompanyUsers.CouldNotRemoveUser"),
            },
          });
          setIsLoading(false);
        });
    }
  };

  return (
    <>
      {!companyState.company?.verified ? (
        <StatusBanner
          iconName="v2/clock"
          label={t("GigsCompany.UnverifiedAccount")}
          subLabel={t("GigsCompany.UnverifiedAccountDetails")}
          textColor={ColorV2.NegativeRed}
          backgroundColor={ColorV2.WithdrawnPink}
          fullWidth
        />
      ) : (
        <>
          <CompanyUsersHeader>
            <CompanyUsersTitle>
              {t("CompanyUsers.CompanyUsers")}
            </CompanyUsersTitle>
            <AddUserButtonSlot>
              <PrimaryButton
                label={t("CompanyUsers.AddUser")}
                iconName="v2/plus"
                onClick={() => setShowAddUserModal(true)}
              />
            </AddUserButtonSlot>
          </CompanyUsersHeader>

          <CompanyUsersList>
            {companyState.users.length === 0 ? (
              <ListMessage>{t("CompanyUsers.None")}</ListMessage>
            ) : (
              companyState.users.map((user) => {
                const canManageUser =
                  user.email !== companyState.companyUser?.email &&
                  companyState.companyUser?.role === UserRole.COMPANY_OWNER;

                return (
                  <UserRow key={user.id}>
                    <AvatarChip title={getDisplayName(user)} />
                    <EmailCell>
                      <LabeledValue
                        label={t("CompanyUsers.Email")}
                        value={user.email}
                        variant="small"
                        truncate
                      />
                    </EmailCell>
                    <UserTypeCell>
                      <LabeledValue
                        label={t("CompanyUsers.Role")}
                        value={t(`CompanyUsers.${user.role}`)}
                        variant="small"
                        truncate
                      />
                    </UserTypeCell>
                    <UserActionsCell>
                      {canManageUser && (
                        <ActionButtons>
                          <TooltipHover text={t("General.Edit")}>
                            <IconButton
                              type="button"
                              aria-label={t("General.Edit")}
                              onClick={() => {
                                setCurrentUser(user);
                                setShowAddUserModal(true);
                              }}
                            >
                              <CustomIcon
                                name="v2/pen"
                                size="20px"
                                color={ColorV2.Blue1}
                              />
                            </IconButton>
                          </TooltipHover>

                          <TooltipHover text={t("General.Delete")}>
                            <IconButton
                              type="button"
                              aria-label={t("General.Delete")}
                              onClick={() => {
                                setCurrentUser(user);
                                setShowDeleteUserModal(true);
                              }}
                            >
                              <CustomIcon
                                name="v2/trash"
                                size="20px"
                                color={ColorV2.NegativeRed}
                              />
                            </IconButton>
                          </TooltipHover>
                        </ActionButtons>
                      )}
                    </UserActionsCell>
                  </UserRow>
                );
              })
            )}
          </CompanyUsersList>

          <Modal
            label={
              currentUser
                ? t("CompanyUsers.UpdateUser")
                : t("CompanyUsers.AddUser")
            }
            isOpen={showAddUserModal}
            onClose={() => {
              setShowAddUserModal(false);
              setCurrentUser(null);
            }}
            size="small"
            showCloseButton={false}
          >
            <CompanyUserForm
              setShowAddUserModal={setShowAddUserModal}
              currentUser={currentUser}
              setCurrentUser={setCurrentUser}
            />
          </Modal>
          <Modal
            label={t("CompanyUsers.RemoveCompanyUser")}
            isOpen={showDeleteUserModal}
            onClose={() => {
              setShowDeleteUserModal(false);
              setCurrentUser(null);
            }}
            size="compact"
            showCloseButton={false}
            footer={
              !isLoading && (
                <ModalActions>
                  <SecondaryButton
                    label={t("General.Cancel")}
                    onClick={() => {
                      setShowDeleteUserModal(false);
                      setCurrentUser(null);
                    }}
                  />

                  <PrimaryButton
                    label={t("General.Remove")}
                    styleOverrides={{
                      backgroundColor: ColorV2.NegativeRed,
                      hoverBackgroundColor: ColorV2.DeclinedRed,
                      activeBackgroundColor: ColorV2.DeclinedRed,
                      textColor: ColorV2.White,
                      borderColor: "transparent",
                      iconColor: ColorV2.White,
                    }}
                    onClick={() =>
                      currentUser && handleRemoveUser(currentUser.id)
                    }
                  />
                </ModalActions>
              )
            }
          >
            {isLoading ? <LoadingContainer /> : null}
          </Modal>
        </>
      )}
    </>
  );
};
